/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect, useLocation } from 'react-router-dom'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import authActions from '@sportninja/common/actions/auth'
import { t } from '@sportninja/common/i18n'
import { EMAIL_LENGTH_MAX, ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { TRIGGER_KEYS } from '../../hooks/useOnboardingTutorial'

import Button from '../../components/ts-components/Button'
import Helmet from '../../components/Helmet'
import CanlanTextLogo from '../../components/icons/CanlanTextLogo'
import SportNinjaLogo from '../../components/ts-components/SportNinjaLogo'
import Text from '../../components/ts-components/Text'
import { TextInput } from '../../components/ts-components/TextInput'
import LoadingSpinner from '../../components/LoadingSpinner'

const VerifyEmail = ({ history, verify, isEmailChange, resend }) => {
  const [step, setStep] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [resendError, setResendError] = useState('')
  const [resendSent, setResendSent] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const { search } = useLocation()
  const params = useMemo(() => {
    const url = /\?token=([^&]+)&context=([^&]+)&context_id=([^&]+)/
    const urlTokenOnly = /\?token=([^&]+)/
    const urlMatch = url.exec(search)
    const tokenMatch = urlTokenOnly.exec(search)

    if (urlMatch) {
      const token = urlMatch?.[1]
      const context = urlMatch?.[2]
      const contextId = urlMatch?.[3]

      return {
        token,
        context,
        contextId,
      }
    }

    if (tokenMatch) {
      const token = tokenMatch?.[1]

      return {
        token,
      }
    }
  }, [search])

  // Run on mount only
  useEffect(() => {
    if (!params?.token || typeof params?.token !== 'string') {
      // error
      setStep(1)
    } else {
      setErrorMessage('')
      verify(params.token)
        .then(() => {
          // Show success message
          setStep(3)
          // We only need to read the user if it's an email change
          if (!isEmailChange) {
            // Wait a bit, then redirect to homepage
            setTimeout(setStep, 2000, 4)
          } else {
            setTimeout(setStep, 2000, 5)
          }
        })
        .catch((e) => {
          // Already verified
          if (
            e.status === 400 &&
            e.message === 'This email has already been verified'
          ) {
            setStep(2)
          } else {
            // Generic error, show 'resend verify email component'
            setStep(1)
            const message = getErrorMessage(e)
            setErrorMessage(
              message
                ? message?.replace('Invalid fields:', '')
                : t('errors:invalidVerifyLink')
            )
          }
        })
    }
  }, [params, isEmailChange])

  const navigator = useMemo(() => {
    if (params?.context === 'registration') {
      return `/registration/${params?.contextId}`
    } else {
      return ROUTES.HOME
    }
  }, [params])

  const goToLogin = useCallback(() => history.push(ROUTES.LOGIN), [history])

  const onResend = async () => {
    if (!email.trim()) {
      setFieldError(t('errors:enterAnEmail'))
      return
    }

    setIsResending(true)
    setResendError('')
    setFieldError('')
    setResendSent(false)

    try {
      await resend(email.trim())
      setResendSent(true)
    } catch (resendFailure) {
      setResendError(getResendErrorMessage(resendFailure))
    } finally {
      setIsResending(false)
    }
  }

  // Set trigger for onboarding modal when redirecting to home after successful verification
  useEffect(() => {
    if (step === 4) {
      sessionStorage.setItem(TRIGGER_KEYS.EMAIL_VERIFIED, 'true')
    }
  }, [step])

  const title = t('Auth:verifyEmail')
  const logo = isCanlan ? (
    <CanlanTextLogo height={32} color={colors.PRIMARY} />
  ) : (
    <div css={styles.sportninjaLogo}>
      <SportNinjaLogo />
    </div>
  )

  if (step === 4) {
    return <Redirect to={navigator} />
  }

  if (step === 5) {
    return <Redirect to={ROUTES.HOME} />
  }

  return (
    <div css={styles.page}>
      <Helmet title={title} />
      <div css={styles.column}>
        <div css={styles.header}>
          <div css={styles.logo}>{logo}</div>
          <Text
            variant='heading2'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={'text-align: center;'}
          >
            {capitalizeWords(t('Auth:startStretching'))}
          </Text>
        </div>
        {
          [
            <div css={styles.status} key='step-0'>
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.NEUTRAL_0}
                style={'text-align: center;'}
              >
                {t('Auth:verifyingAccount')}...
              </Text>
              <div css={styles.loader}>
                <LoadingSpinner size={3} />
              </div>
            </div>,

            <Fragment key='step-1'>
              <div css={styles.status}>
                <Text
                  variant='paragraphMedium'
                  weight='regular'
                  color={colors.ERROR_LIGHT}
                  style={'text-align: center;'}
                >
                  {errorMessage || t('errors:invalidVerifyLink')}
                </Text>
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style={'text-align: center;'}
                >
                  {t('Auth:enterYourEmailAddress')}
                </Text>
              </div>
              <form
                css={styles.form}
                noValidate
                onSubmit={(event) => {
                  event.preventDefault()
                  onResend()
                }}
              >
                {resendError ? (
                  <div css={styles.banner} role='alert'>
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.ERROR_LIGHT}
                    >
                      {resendError}
                    </Text>
                  </div>
                ) : null}
                <div css={styles.field}>
                  <TextInput
                    name='email'
                    value={email}
                    setValue={setEmail}
                    fieldErrors={fieldError ? { email: fieldError } : {}}
                    customError={null}
                    label={capitalizeWords(t('common:email'))}
                    placeholder={capitalizeWords(t('common:email'))}
                    type='email'
                    maxLength={EMAIL_LENGTH_MAX}
                    disabled={isResending}
                    error={Boolean(fieldError)}
                    required
                  />
                </div>
                <Button
                  text={t('Auth:resendEmail')}
                  variant='primary'
                  onClick={onResend}
                  isLoading={isResending}
                  disabled={isResending}
                  style={styles.submit}
                />
                {resendSent ? (
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_0}
                    style={'text-align: center;'}
                  >
                    {t('Auth:confirmEmailSent')}
                  </Text>
                ) : null}
              </form>
              <div css={styles.footer}>
                <div css={styles.rule} />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style={'text-align: center;'}
                >
                  {t('Auth:orIfYouAlreadyHaveAnAccount')}{' '}
                  <button css={styles.link} type='button' onClick={goToLogin}>
                    {capitalizeWords(t('common:login'))}
                  </button>
                </Text>
              </div>
            </Fragment>,

            <div css={styles.status} key='step-2'>
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.NEUTRAL_0}
                style={'text-align: center;'}
              >
                {t('errors:emailAlreadyVerified')}
              </Text>
              <Button
                text={t('Auth:goToLogin')}
                variant='primary'
                onClick={goToLogin}
                style={styles.submit}
              />
            </div>,

            <div css={styles.status} key='step-3'>
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.NEUTRAL_0}
                style={'text-align: center;'}
              >
                {isEmailChange
                  ? 'Email changed successfully. Redirecting you to the login page...'
                  : t('Auth:thanksForVerifying')}
              </Text>
              <div css={styles.loader}>
                <LoadingSpinner size={3} />
              </div>
            </div>,
          ][step]
        }
      </div>
    </div>
  )
}

VerifyEmail.propTypes = {
  location: PropTypes.object.isRequired,
  verify: PropTypes.func.isRequired,
}

const getResendErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return t('errors:somethingWentWrongPleaseTryAgain')
  }

  if (error.status === 400 && error.invalid_fields?.validated_at) {
    return t('errors:emailAlreadyVerified')
  }
  if (error.status === 404) {
    return t('errors:couldNotFindAccount')
  }

  return error.message || t('errors:somethingWentWrongPleaseTryAgain')
}

const mapDispatchToProps = (dispatch) => ({
  resend: bindActionToPromise(dispatch, authActions.resend.request),
})

export default connect(null, mapDispatchToProps)(VerifyEmail)

const styles = {
  page: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 16px;
    padding-block: 48px;
    background: ${colors.SECONDARY_400};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    max-width: 650px;
  `,
  header: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;
  `,
  logo: css`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
  `,
  sportninjaLogo: css`
    display: flex;

    svg {
      height: 48px;
      width: auto;
      max-width: 100%;
    }
  `,
  status: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
  `,
  loader: css`
    display: flex;
    justify-content: center;
    width: 100%;
  `,
  footer: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `,
  rule: css`
    height: 1px;
    background: ${colors.SECONDARY_300};
  `,
  link: css`
    color: ${colors.PRIMARY};
    text-decoration: underline;
    text-underline-offset: 2px;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  `,
  submit: css`
    width: 100%;
    flex: none;
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  `,
  banner: css`
    border-radius: 8px;
    border: 1px solid ${colors.ERROR_300};
    background: rgba(217, 27, 27, 0.12);
    padding: 8px 12px;
  `,
}
